import { BaseLogicExpression } from "./BaseLogic";

import { ExecutionData } from "../../../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../../../Common/Testing/ITestingContext";
import { DataUtilities } from "../../../Common/DataUtilities";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { ValidationIssue } from "../../../Common/IDesignValidationProvider";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

export class ExistsExpression extends BaseLogicExpression {
    readonly __type: string = "Exists";
    dataPath?: string;

    evaluate(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        if (!this.dataPath) {
            throw new Error("dataPath is not set");
        }

        let field: IDataDefinition | undefined =
            this.expressionContext?.definition?.requiredVariable?.name === this.dataPath
                ? this.expressionContext?.definition?.requiredVariable
                : undefined;

        if (!field) {
            let dataDefinition = this.expressionContext?.definition?.getInputDefinition();
            if (!dataDefinition) {
                throw new Error("can not get the data definition as edit context or expression definition is not set");
            }
            field = DataUtilities.getFieldFromDataDefinitionByName(
                dataDefinition.fields,
                this.dataPath,
            ) as IDataDefinition;
        }

        const result = DataUtilities.get(data.values, this.dataPath);

        if (field.list) {
            if (Array.isArray(result)) {
                return result.length > 0;
            }
        } else {
            return result !== null && result !== undefined;
        }
    }

    getStorageData(): object {
        let data = super.getStorageData();
        Reflect.set(data, "dataPath", this.dataPath);
        return data;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        Reflect.set(this, "dataPath", Reflect.get(data, "dataPath"));
    }

    getValidationIssues(): ValidationIssue[] {
        if (!this.dataPath) {
            return [new ValidationIssue("data path is not set")];
        }

        if (
            this.expressionContext?.definition?.requiredVariable &&
            this.expressionContext?.definition?.requiredVariable.name === this.dataPath
        ) {
            return [];
        }

        let dataDefinition = this.expressionContext?.definition?.getInputDefinition();
        if (!dataDefinition) {
            return [
                new ValidationIssue(
                    "can not get the data definition as edit context or expression definition is not set",
                ),
            ];
        }

        try {
            let field = DataUtilities.getFieldFromDataDefinitionByName(
                dataDefinition.fields,
                this.dataPath,
            ) as IDataDefinition;

            if (!field) {
                return [new ValidationIssue("invalid data path")];
            }

            return [];
        } catch (e) {
            return [new ValidationIssue("invalid data path")];
        }
    }
}
